// -----------------------------------------------------------------------------
// Deterministic mock data for the Reports pages (Vehicle, Part Sales,
// Inventory, Salesman, Dismantler).
//
// The backend endpoints for these reports don't exist yet (see the new
// `/report/:type` block in `docs/pending-endpoints.md`), so for now each page
// renders seeded, realistic-looking data. Using the same fixtures everywhere
// means the numbers line up across reports — e.g. a sale in the Part Sales
// report feeds into its salesperson's totals on the Salesman report, and the
// vehicle whose part was dismantled appears in the Dismantler report.
//
// Swap `getReportData*` for React Query hooks once the API ships.
// -----------------------------------------------------------------------------

// Small, fast, deterministic PRNG. Same seed → same stream every render,
// which keeps tile totals stable while the page is open (no "my stat
// changed between renders" flicker) and also across reloads.
export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

const pickFactory = <T>(rand: () => number) =>
  (arr: readonly T[]): T => arr[Math.floor(rand() * arr.length)];

// ---------------------------------------------------------------------------
// Fixtures — curated so demos feel grounded. Real makes/models, a plausible
// mix of salespeople and dismantlers, and part categories our real data
// also contains.
// ---------------------------------------------------------------------------

export interface MockVehicle {
  id: string;
  stockNumber: string;
  registration: string;
  make: string;
  model: string;
  derivative: string;
  year: number;
  colour: string;
  mileage: number;
  fuelType: "Petrol" | "Diesel" | "Hybrid" | "Electric";
  bodyStyle: string;
  collectedOn: string;
  purchasePrice: number;
  /** Headline sale price of the donor — used by the Vehicle report ROI. */
  estimatedValue: number;
}

const VEHICLE_FIXTURES: MockVehicle[] = [
  { id: "v-001", stockNumber: "WM-23811", registration: "AB21 LMO", make: "Ford",          model: "Kuga",     derivative: "ST-Line X 2.0 EcoBlue", year: 2021, colour: "Magnetic Grey",    mileage:  38_250, fuelType: "Diesel",   bodyStyle: "SUV",       collectedOn: "2026-01-14", purchasePrice: 1650, estimatedValue: 6200 },
  { id: "v-002", stockNumber: "WM-23814", registration: "VN19 TXR", make: "Volkswagen",    model: "Golf",     derivative: "GT TSI EVO 150",       year: 2019, colour: "Tornado Red",      mileage:  54_890, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-01-18", purchasePrice: 1200, estimatedValue: 5400 },
  { id: "v-003", stockNumber: "WM-23820", registration: "KS68 PHT", make: "BMW",           model: "3 Series", derivative: "320d M Sport xDrive",  year: 2018, colour: "Alpine White",     mileage:  72_410, fuelType: "Diesel",   bodyStyle: "Saloon",    collectedOn: "2026-01-21", purchasePrice: 2100, estimatedValue: 7800 },
  { id: "v-004", stockNumber: "WM-23830", registration: "GK70 VXO", make: "Audi",          model: "A3",       derivative: "Sportback 35 TFSI",    year: 2020, colour: "Mythos Black",     mileage:  29_650, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-01-25", purchasePrice: 1900, estimatedValue: 6600 },
  { id: "v-005", stockNumber: "WM-23841", registration: "PL22 CSA", make: "Nissan",        model: "Qashqai",  derivative: "Tekna DIG-T 158",      year: 2022, colour: "Ceramic Grey",     mileage:  18_410, fuelType: "Petrol",   bodyStyle: "SUV",       collectedOn: "2026-01-29", purchasePrice: 2400, estimatedValue: 7200 },
  { id: "v-006", stockNumber: "WM-23856", registration: "SY17 WBT", make: "Vauxhall",      model: "Astra",    derivative: "Elite Nav 1.4T",       year: 2017, colour: "Power Red",        mileage:  81_020, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-02-02", purchasePrice:  820, estimatedValue: 3400 },
  { id: "v-007", stockNumber: "WM-23862", registration: "EA23 RJT", make: "Mercedes",      model: "A-Class",  derivative: "A 180 AMG Line Exec",  year: 2023, colour: "Cosmos Black",     mileage:   9_880, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-02-05", purchasePrice: 3100, estimatedValue: 9600 },
  { id: "v-008", stockNumber: "WM-23874", registration: "OE15 JVA", make: "Ford",          model: "Fiesta",   derivative: "Zetec 1.0 EcoBoost",   year: 2015, colour: "Panther Black",    mileage: 112_200, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-02-08", purchasePrice:  430, estimatedValue: 1800 },
  { id: "v-009", stockNumber: "WM-23890", registration: "TR69 KWE", make: "Land Rover",    model: "Discovery",derivative: "Sport HSE SD4 TD",     year: 2019, colour: "Fuji White",       mileage:  68_430, fuelType: "Diesel",   bodyStyle: "SUV",       collectedOn: "2026-02-12", purchasePrice: 2900, estimatedValue: 9800 },
  { id: "v-010", stockNumber: "WM-23902", registration: "BF18 YSW", make: "Volkswagen",    model: "Passat",   derivative: "GT 2.0 TDI 150",       year: 2018, colour: "Reflex Silver",    mileage:  92_820, fuelType: "Diesel",   bodyStyle: "Estate",    collectedOn: "2026-02-16", purchasePrice: 1450, estimatedValue: 5200 },
  { id: "v-011", stockNumber: "WM-23911", registration: "DK22 AVT", make: "Hyundai",       model: "Tucson",   derivative: "Premium SE MHEV",      year: 2022, colour: "Phantom Black",    mileage:  20_410, fuelType: "Hybrid",   bodyStyle: "SUV",       collectedOn: "2026-02-20", purchasePrice: 2650, estimatedValue: 7400 },
  { id: "v-012", stockNumber: "WM-23925", registration: "JN70 MCX", make: "Kia",           model: "Sportage", derivative: "GT-Line S 1.6 CRDi",   year: 2020, colour: "Infra Red",        mileage:  47_110, fuelType: "Diesel",   bodyStyle: "SUV",       collectedOn: "2026-02-24", purchasePrice: 1750, estimatedValue: 6400 },
  { id: "v-013", stockNumber: "WM-23940", registration: "UG66 BYH", make: "Toyota",        model: "Auris",    derivative: "Icon Hybrid",          year: 2016, colour: "Atlantic Blue",    mileage:  96_330, fuelType: "Hybrid",   bodyStyle: "Hatchback", collectedOn: "2026-02-28", purchasePrice:  620, estimatedValue: 2400 },
  { id: "v-014", stockNumber: "WM-23955", registration: "WC21 OAN", make: "BMW",           model: "1 Series", derivative: "118i M Sport",         year: 2021, colour: "Mineral Grey",     mileage:  31_800, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-03-03", purchasePrice: 2250, estimatedValue: 7100 },
  { id: "v-015", stockNumber: "WM-23968", registration: "LF14 ZDU", make: "Honda",         model: "Civic",    derivative: "SR 1.8 i-VTEC",        year: 2014, colour: "Cosmic Grey",      mileage: 128_540, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-03-06", purchasePrice:  380, estimatedValue: 1650 },
  { id: "v-016", stockNumber: "WM-23982", registration: "GR71 MKB", make: "Audi",          model: "Q5",       derivative: "TFSI Quattro S Line",  year: 2021, colour: "Glacier White",    mileage:  26_710, fuelType: "Petrol",   bodyStyle: "SUV",       collectedOn: "2026-03-10", purchasePrice: 3400, estimatedValue: 11500 },
  { id: "v-017", stockNumber: "WM-23995", registration: "HY19 PAM", make: "Peugeot",       model: "3008",     derivative: "GT Line 1.5 BlueHDi",  year: 2019, colour: "Pearl White",      mileage:  62_920, fuelType: "Diesel",   bodyStyle: "SUV",       collectedOn: "2026-03-13", purchasePrice: 1320, estimatedValue: 4950 },
  { id: "v-018", stockNumber: "WM-24011", registration: "MR23 EFL", make: "Tesla",         model: "Model 3",  derivative: "Long Range AWD",       year: 2023, colour: "Midnight Silver",  mileage:  11_430, fuelType: "Electric", bodyStyle: "Saloon",    collectedOn: "2026-03-17", purchasePrice: 4100, estimatedValue: 14200 },
  { id: "v-019", stockNumber: "WM-24024", registration: "SP16 JGB", make: "Ford",          model: "Mondeo",   derivative: "Titanium X 2.0 TDCi",  year: 2016, colour: "Shadow Black",     mileage: 104_310, fuelType: "Diesel",   bodyStyle: "Estate",    collectedOn: "2026-03-20", purchasePrice:  610, estimatedValue: 2700 },
  { id: "v-020", stockNumber: "WM-24038", registration: "AE72 OVM", make: "Renault",       model: "Clio",     derivative: "RS Line TCe 130 EDC",  year: 2022, colour: "Flame Red",        mileage:  16_220, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-03-24", purchasePrice: 1250, estimatedValue: 4200 },
  { id: "v-021", stockNumber: "WM-24051", registration: "CR20 NJH", make: "Seat",          model: "Leon",     derivative: "FR TSI EVO",           year: 2020, colour: "Pyrenees Grey",    mileage:  41_880, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-03-28", purchasePrice: 1180, estimatedValue: 4400 },
  { id: "v-022", stockNumber: "WM-24063", registration: "YP68 RKE", make: "Skoda",         model: "Octavia",  derivative: "SE L TDI DSG",         year: 2018, colour: "Steel Grey",       mileage:  73_640, fuelType: "Diesel",   bodyStyle: "Estate",    collectedOn: "2026-04-01", purchasePrice: 1020, estimatedValue: 4100 },
  { id: "v-023", stockNumber: "WM-24077", registration: "UM17 WTR", make: "Mazda",         model: "CX-5",     derivative: "Sport Nav+ AWD",       year: 2017, colour: "Soul Red Crystal", mileage:  88_220, fuelType: "Diesel",   bodyStyle: "SUV",       collectedOn: "2026-04-04", purchasePrice: 1110, estimatedValue: 4600 },
  { id: "v-024", stockNumber: "WM-24089", registration: "OX23 AMP", make: "Mini",          model: "Cooper S", derivative: "Sport 3-Door",         year: 2023, colour: "Chili Red",        mileage:   7_940, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-04-08", purchasePrice: 2800, estimatedValue: 8100 },
  { id: "v-025", stockNumber: "WM-24101", registration: "PD19 LSH", make: "Jaguar",        model: "XE",       derivative: "R-Sport 2.0 D180",     year: 2019, colour: "Santorini Black",  mileage:  55_330, fuelType: "Diesel",   bodyStyle: "Saloon",    collectedOn: "2026-04-11", purchasePrice: 1600, estimatedValue: 6250 },
  { id: "v-026", stockNumber: "WM-24118", registration: "NE22 CVK", make: "Volvo",         model: "XC60",     derivative: "R-Design B4 AWD",      year: 2022, colour: "Denim Blue",       mileage:  23_170, fuelType: "Hybrid",   bodyStyle: "SUV",       collectedOn: "2026-04-14", purchasePrice: 3200, estimatedValue: 10600 },
  { id: "v-027", stockNumber: "WM-24132", registration: "FT15 SOW", make: "Kia",           model: "Rio",      derivative: "SR7 1.25",             year: 2015, colour: "Signal Red",       mileage: 118_400, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-04-18", purchasePrice:  280, estimatedValue: 1150 },
  { id: "v-028", stockNumber: "WM-24145", registration: "DV24 BSC", make: "Range Rover",   model: "Evoque",   derivative: "SE Dynamic P300e",     year: 2024, colour: "Firenze Red",      mileage:   4_100, fuelType: "Hybrid",   bodyStyle: "SUV",       collectedOn: "2026-04-20", purchasePrice: 4400, estimatedValue: 14400 },
  { id: "v-029", stockNumber: "WM-24156", registration: "GH21 PXL", make: "Citroen",       model: "C4",       derivative: "Shine Plus PureTech",  year: 2021, colour: "Polar White",      mileage:  34_620, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-04-22", purchasePrice: 1050, estimatedValue: 3900 },
  { id: "v-030", stockNumber: "WM-24172", registration: "BS20 VKA", make: "Hyundai",       model: "i30",      derivative: "N Performance",        year: 2020, colour: "Performance Blue", mileage:  48_210, fuelType: "Petrol",   bodyStyle: "Hatchback", collectedOn: "2026-04-23", purchasePrice: 2150, estimatedValue: 7800 },
];

export interface MockPerson {
  id: string;
  name: string;
  initials: string;
  role: "Sales" | "Dismantling" | "Both";
}

export const SALESPEOPLE: MockPerson[] = [
  { id: "sp-1", name: "Adam Pile",        initials: "AP", role: "Sales" },
  { id: "sp-2", name: "Jordan Miller",    initials: "JM", role: "Sales" },
  { id: "sp-3", name: "Priya Shah",       initials: "PS", role: "Sales" },
  { id: "sp-4", name: "Olivia Evans",     initials: "OE", role: "Sales" },
  { id: "sp-5", name: "Ben Carter",       initials: "BC", role: "Sales" },
  { id: "sp-6", name: "Hanna Lindqvist",  initials: "HL", role: "Sales" },
  { id: "sp-7", name: "Marcus Johnson",   initials: "MJ", role: "Both" },
  { id: "sp-8", name: "Yasmin Ahmed",     initials: "YA", role: "Both" },
];

export const DISMANTLERS: MockPerson[] = [
  { id: "dm-1", name: "Kieran Doyle",     initials: "KD", role: "Dismantling" },
  { id: "dm-2", name: "Lewis Thompson",   initials: "LT", role: "Dismantling" },
  { id: "dm-3", name: "Ravi Patel",       initials: "RP", role: "Dismantling" },
  { id: "dm-4", name: "Ethan Walker",     initials: "EW", role: "Dismantling" },
  { id: "dm-5", name: "Sofia Romano",     initials: "SR", role: "Dismantling" },
  { id: "dm-6", name: "Callum Reid",      initials: "CR", role: "Dismantling" },
  { id: "sp-7", name: "Marcus Johnson",   initials: "MJ", role: "Both" },
  { id: "sp-8", name: "Yasmin Ahmed",     initials: "YA", role: "Both" },
];

export interface MockCategory {
  id: string;
  name: string;
  /** Rough price band (£) used by the mock pricing model. */
  priceBand: [number, number];
  /** Average dismantling time per part (minutes). */
  minutesPerPart: number;
}

export const PART_CATEGORIES: MockCategory[] = [
  { id: "pc-engine",     name: "Engine (Complete)",   priceBand: [450, 1800], minutesPerPart: 180 },
  { id: "pc-gearbox",    name: "Gearbox / Transmission", priceBand: [250, 950],  minutesPerPart: 90 },
  { id: "pc-turbo",      name: "Turbocharger",        priceBand: [180, 720],  minutesPerPart: 40 },
  { id: "pc-alternator", name: "Alternator",          priceBand: [45, 180],   minutesPerPart: 12 },
  { id: "pc-starter",    name: "Starter Motor",       priceBand: [30, 140],   minutesPerPart: 10 },
  { id: "pc-ecu",        name: "Engine ECU",          priceBand: [90, 260],   minutesPerPart: 20 },
  { id: "pc-seat",       name: "Seat",                priceBand: [60, 220],   minutesPerPart: 25 },
  { id: "pc-door",       name: "Door (Bare Shell)",   priceBand: [90, 320],   minutesPerPart: 35 },
  { id: "pc-bonnet",     name: "Bonnet",              priceBand: [60, 260],   minutesPerPart: 15 },
  { id: "pc-bumper",     name: "Bumper (Front)",      priceBand: [50, 220],   minutesPerPart: 18 },
  { id: "pc-bumper-r",   name: "Bumper (Rear)",       priceBand: [45, 200],   minutesPerPart: 18 },
  { id: "pc-headlight",  name: "Headlight",           priceBand: [45, 280],   minutesPerPart: 8 },
  { id: "pc-wing",       name: "Wing Panel",          priceBand: [50, 180],   minutesPerPart: 18 },
  { id: "pc-tailgate",   name: "Tailgate",            priceBand: [90, 340],   minutesPerPart: 25 },
  { id: "pc-suspension", name: "Suspension Strut",    priceBand: [50, 180],   minutesPerPart: 12 },
  { id: "pc-wheel",      name: "Alloy Wheel",         priceBand: [45, 180],   minutesPerPart: 8 },
  { id: "pc-airbag",     name: "Airbag Module",       priceBand: [60, 240],   minutesPerPart: 15 },
  { id: "pc-mirror",     name: "Wing Mirror",         priceBand: [25, 110],   minutesPerPart: 6 },
  { id: "pc-radio",      name: "Infotainment Head Unit", priceBand: [60, 280],   minutesPerPart: 15 },
  { id: "pc-aircon",     name: "A/C Compressor",      priceBand: [80, 260],   minutesPerPart: 20 },
];

export const SALES_CHANNELS = ["eBay", "Walk-in", "Phone", "Trade", "Website"] as const;
export const WAREHOUSE_BINS = [
  "A-01", "A-02", "A-03", "B-11", "B-12", "B-13", "C-21", "C-22", "D-31", "D-32", "E-41", "E-42",
] as const;
export const STOCK_STATUSES = ["In Stock", "Reserved", "On Sale", "Missing"] as const;

// ---------------------------------------------------------------------------
// Generators
// ---------------------------------------------------------------------------

export interface MockPartSale {
  id: string;
  saleDate: string; // ISO
  orderNumber: string;
  sku: string;
  partName: string;
  partCategoryId: string;
  partCategory: string;
  vehicleId: string;
  vehicle: MockVehicle;
  salespersonId: string;
  salesperson: MockPerson;
  salesChannel: (typeof SALES_CHANNELS)[number];
  quantity: number;
  unitPrice: number;
  cost: number;
  total: number;
  profit: number;
}

const ymd = (d: Date) => d.toISOString().slice(0, 10);

const randomDateBetween = (rand: () => number, fromIso: string, toIso: string) => {
  const from = Date.parse(fromIso);
  const to = Date.parse(toIso);
  return new Date(from + Math.floor(rand() * Math.max(1, to - from))).toISOString();
};

/**
 * Builds a year's worth of part sales — ~700 events by default, spread across
 * every vehicle, salesperson and part category so aggregate reports look
 * realistic. Seed-stable per (from,to,size) tuple.
 */
export function buildPartSales(
  fromIso: string,
  toIso: string,
  size = 700,
  seed = 0xA11CE
): MockPartSale[] {
  const rand = mulberry32(seed);
  const pick = pickFactory<unknown>(rand);
  const rows: MockPartSale[] = [];

  for (let i = 0; i < size; i++) {
    const vehicle = pick(VEHICLE_FIXTURES) as MockVehicle;
    const category = pick(PART_CATEGORIES) as MockCategory;
    const salesperson = pick(SALESPEOPLE) as MockPerson;
    const channel = pick(SALES_CHANNELS) as (typeof SALES_CHANNELS)[number];

    const [lo, hi] = category.priceBand;
    const unitPrice = Math.round(lo + rand() * (hi - lo));
    const quantity = 1 + (rand() < 0.88 ? 0 : Math.floor(rand() * 2) + 1);
    const cost = Math.round(unitPrice * (0.15 + rand() * 0.3));
    const total = unitPrice * quantity;
    const profit = total - cost * quantity;

    const sku = `${vehicle.stockNumber}-${category.id
      .replace("pc-", "")
      .toUpperCase()
      .slice(0, 4)}-${(i + 1).toString().padStart(4, "0")}`;

    const orderNumber = `ORD-${(200000 + i).toString().padStart(6, "0")}`;
    const saleDate = randomDateBetween(rand, fromIso, toIso);

    rows.push({
      id: `sale-${i}`,
      saleDate,
      orderNumber,
      sku,
      partName: `${category.name} (${vehicle.make} ${vehicle.model})`,
      partCategoryId: category.id,
      partCategory: category.name,
      vehicleId: vehicle.id,
      vehicle,
      salespersonId: salesperson.id,
      salesperson,
      salesChannel: channel,
      quantity,
      unitPrice,
      cost,
      total,
      profit,
    });
  }

  // Sort newest first so tables default to "most recent".
  rows.sort((a, b) => Date.parse(b.saleDate) - Date.parse(a.saleDate));
  return rows;
}

export interface MockInventoryRow {
  id: string;
  sku: string;
  partName: string;
  partCategoryId: string;
  partCategory: string;
  vehicleId: string;
  vehicle: MockVehicle;
  bin: string;
  quantity: number;
  unitPrice: number;
  totalValue: number;
  status: (typeof STOCK_STATUSES)[number];
  addedOn: string;
  daysInStock: number;
}

export function buildInventory(
  size = 420,
  seed = 0xB0FF1E,
  asOf: Date = new Date()
): MockInventoryRow[] {
  const rand = mulberry32(seed);
  const pick = pickFactory<unknown>(rand);
  const rows: MockInventoryRow[] = [];

  const asOfMs = asOf.getTime();

  for (let i = 0; i < size; i++) {
    const vehicle = pick(VEHICLE_FIXTURES) as MockVehicle;
    const category = pick(PART_CATEGORIES) as MockCategory;
    const [lo, hi] = category.priceBand;
    const unitPrice = Math.round(lo + rand() * (hi - lo));
    const quantity = 1 + (rand() < 0.9 ? 0 : Math.floor(rand() * 3) + 1);

    // Distribution: mostly in stock, with a sprinkling of reserved/on sale
    // and the odd missing item to keep the Missing stat non-zero.
    const status =
      rand() < 0.75
        ? "In Stock"
        : rand() < 0.55
          ? "Reserved"
          : rand() < 0.6
            ? "On Sale"
            : "Missing";

    const daysInStock = Math.floor(rand() * 180);
    const added = new Date(asOfMs - daysInStock * 86_400_000);

    const sku = `${vehicle.stockNumber}-${category.id
      .replace("pc-", "")
      .toUpperCase()
      .slice(0, 4)}-${(i + 1).toString().padStart(4, "0")}`;

    rows.push({
      id: `inv-${i}`,
      sku,
      partName: `${category.name} (${vehicle.make} ${vehicle.model})`,
      partCategoryId: category.id,
      partCategory: category.name,
      vehicleId: vehicle.id,
      vehicle,
      bin: pick(WAREHOUSE_BINS) as string,
      quantity,
      unitPrice,
      totalValue: unitPrice * quantity,
      status: status as (typeof STOCK_STATUSES)[number],
      addedOn: ymd(added),
      daysInStock,
    });
  }

  rows.sort((a, b) => a.partCategory.localeCompare(b.partCategory));
  return rows;
}

export interface MockDismantleEvent {
  id: string;
  dismantledDate: string; // ISO
  vehicleId: string;
  vehicle: MockVehicle;
  dismantlerId: string;
  dismantler: MockPerson;
  partCategoryId: string;
  partCategory: string;
  partName: string;
  quantity: number;
  durationMinutes: number;
  outcome: "Saleable" | "Damaged" | "Scrap";
  notes: string;
}

const OUTCOME_NOTES: Record<MockDismantleEvent["outcome"], string[]> = {
  Saleable: ["Photographed and logged", "Cleaned, ready for listing", "Binned in stock"],
  Damaged:  ["Scuff on finish", "Hairline crack on housing", "Minor rub on edge"],
  Scrap:    ["Corroded beyond re-use", "Damaged on removal", "Mount sheared — not viable"],
};

export function buildDismantleEvents(
  fromIso: string,
  toIso: string,
  size = 520,
  seed = 0xC0FFEE
): MockDismantleEvent[] {
  const rand = mulberry32(seed);
  const pick = pickFactory<unknown>(rand);
  const rows: MockDismantleEvent[] = [];

  for (let i = 0; i < size; i++) {
    const vehicle = pick(VEHICLE_FIXTURES) as MockVehicle;
    const category = pick(PART_CATEGORIES) as MockCategory;
    const dismantler = pick(DISMANTLERS) as MockPerson;

    // Duration varies around the category's expected minutes so stats
    // feel honest — some parts come off fast, some fight you.
    const baseMin = category.minutesPerPart;
    const durationMinutes = Math.max(
      3,
      Math.round(baseMin * (0.6 + rand() * 1.1) + rand() * 10)
    );

    const outcome: MockDismantleEvent["outcome"] =
      rand() < 0.85 ? "Saleable" : rand() < 0.67 ? "Damaged" : "Scrap";

    const notesArr = OUTCOME_NOTES[outcome];
    const notes = notesArr[Math.floor(rand() * notesArr.length)];

    rows.push({
      id: `dis-${i}`,
      dismantledDate: randomDateBetween(rand, fromIso, toIso),
      vehicleId: vehicle.id,
      vehicle,
      dismantlerId: dismantler.id,
      dismantler,
      partCategoryId: category.id,
      partCategory: category.name,
      partName: `${category.name} (${vehicle.make} ${vehicle.model})`,
      quantity: 1,
      durationMinutes,
      outcome,
      notes,
    });
  }

  rows.sort((a, b) => Date.parse(b.dismantledDate) - Date.parse(a.dismantledDate));
  return rows;
}

// Dates to show/filter within. We anchor to the 12-month window preceding
// `today` so navigating between reports the window is naturally consistent.
export const today = () => new Date().toISOString().slice(0, 10);
export const monthsAgo = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

export function VEHICLES(): MockVehicle[] {
  return VEHICLE_FIXTURES;
}
